import { FilterAlt, FilterList } from '@mui/icons-material'
import { 
    FormControl, 
    InputLabel, 
    MenuItem, 
    Select, 
    Stack, 
    Box,
    useTheme,
    useMediaQuery,
    IconButton,
    Drawer,
    Typography,
    Button,
    Divider
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import MarkAllAsRead from './MarkAllAsRead'

const starRatings = {
    all: "All",
    5: "5",
    4: "4",
    3: "3",
    2: "2",
    1: "1",
}

const status = {
    all: "All",
    read: "Read",
    unread: "Unread"
}

export const ratingParams = {
    read: "read",
    rating: "rating"
}

export default function MasterRatingFilter() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  function handleSetReadFilter(val) {
    if (String(val).toLocaleLowerCase() == String(status.read).toLocaleLowerCase()) {
        searchParams.set(ratingParams.read, String(status.read).toLocaleLowerCase())
    } else if (String(val).toLocaleLowerCase() == String(status.unread).toLocaleLowerCase()){
        searchParams.set(ratingParams.read, String(status.unread).toLocaleLowerCase())
    } else {
        searchParams.delete(ratingParams.read)
    }
    setSearchParams(searchParams)
    if (isMobile) setFilterDrawerOpen(false);
  }

  function handleSetRatingFilter(val) {
    if (isNaN(val)) {
        searchParams.delete(ratingParams.rating);
    } else {
        searchParams.set(ratingParams.rating, val);
    }
    setSearchParams(searchParams)
    if (isMobile) setFilterDrawerOpen(false);
  }

  const currentRatingFilter = searchParams.get(ratingParams.rating) ?? String(starRatings.all).toLocaleLowerCase();
  const currentStatusFilter = searchParams.get(ratingParams.read) ?? String(status.all).toLocaleLowerCase();

  // Mobile Filter Drawer
  const FilterDrawer = () => (
    <Drawer
      anchor="bottom"
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          {t('Filters')}
        </Typography>
        
        <Stack spacing={3}>
          <FormControl fullWidth size="medium">
            <InputLabel>{t("Rating")}</InputLabel>
            <Select
              value={currentRatingFilter}
              label={t("Rating")}
              onChange={(e) => handleSetRatingFilter(e.target.value)}
            >
              {Object.entries(starRatings).map(([key, value]) => (
                <MenuItem key={key} value={key}>{t(value)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="medium">
            <InputLabel>{t("Status")}</InputLabel>
            <Select
              value={currentStatusFilter}
              label={t("Status")}
              onChange={(e) => handleSetReadFilter(e.target.value)}
            >
              {Object.entries(status).map(([key, value]) => (
                <MenuItem key={key} value={key}>{t(value)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              color='error'
              onClick={() => setFilterDrawerOpen(false)}
              size="large"
            >
              {t('Cancel')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Drawer>
  );

  // Mobile Chip Display
  const MobileFilterChips = () => {
    const activeFilters = [];

    if (currentRatingFilter !== "all") {
      activeFilters.push(
        `${t("Rating")}: ${t(starRatings[currentRatingFilter])}`
      );
    }

    if (currentStatusFilter !== "all") {
      activeFilters.push(`${t("Status")}: ${t(status[currentStatusFilter])}`);
    }

    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <IconButton onClick={() => setFilterDrawerOpen(true)} title={t("Filter")}>
          <FilterList />
        </IconButton>
      </Box>
    );
  };

  // Desktop View
  if (!isMobile) {
    return (
      <Stack spacing={2} direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FilterAlt color="action" />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t("Rating")}</InputLabel>
            <Select
              value={currentRatingFilter}
              label={t("Rating")}
              onChange={(e) => handleSetRatingFilter(e.target.value)}
            >
              {Object.entries(starRatings).map(([key, value]) => (
                <MenuItem key={key} value={key}>{t(value)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t("Status")}</InputLabel>
            <Select
              value={currentStatusFilter}
              label={t("Status")}
              onChange={(e) => handleSetReadFilter(e.target.value)}
            >
              {Object.entries(status).map(([key, value]) => (
                <MenuItem key={key} value={key}>{t(value)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <MarkAllAsRead />
      </Stack>
    );
  }

  // Mobile View
  return (
    <Box>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ p: 1 }}
      >
        <MobileFilterChips />
        <MarkAllAsRead />
      </Stack>
      <FilterDrawer />
    </Box>
  );
}